import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  DocumentReference,
  FieldValue,
  deleteField,
} from "firebase/firestore";
import { db } from "./init";
import { VideoMeta } from "@/types/video";
import { convertTimestampToDate } from "./firestore";

/**
 * 轉換 Firestore 數據為 VideoMeta
 */
function convertVideoData(data: any): Omit<VideoMeta, "id"> {
  return {
    vid: data.vid || "",
    drama_id: data.drama_id || "",
    order: data.order ?? 0,
    vip: data.vip ?? false,
    episode_unlock_coin_cost: data.episode_unlock_coin_cost,
    episode_label: data.episode_label,
    episode_type: data.episode_type,
    caption: data.caption,
    name: data.name,
    duration: data.duration,
    cover_url: data.cover_url,
    display_type: data.display_type,
    play_times: data.play_times ?? 0,
    height: data.height,
    width: data.width,
    // Firestore 統一 likeCount；保留 like 僅為舊資料 fallback
    like: data.likeCount ?? data.like ?? 0,
    comment: data.comment,
    scheduled_release_time: convertTimestampToDate(data.scheduled_release_time),
    scheduled_release_timezone: data.scheduled_release_timezone,
    scheduled_release_time_display: data.scheduled_release_time_display,
    is_released: data.is_released,
    release_status: data.release_status,
    upload_status: data.upload_status,
    upload_error: data.upload_error,
    workflow_status: data.workflow_status,
    workflow_run_id: data.workflow_run_id,
    workflow_template_id: data.workflow_template_id,
    workflow_completed_at: convertTimestampToDate(data.workflow_completed_at),
    createdAt: convertTimestampToDate(data.createdAt),
    updatedAt: convertTimestampToDate(data.updatedAt),
  };
}

/**
 * 通過 DocumentReference 陣列獲取影集（主要方式）
 */
export async function getVideosByReferences(
  references: DocumentReference[]
): Promise<(VideoMeta & { id: string })[]> {
  try {
    if (!references || references.length === 0) {
      return [];
    }

    const videoSnaps = await Promise.all(
      references.map((ref) => getDoc(ref))
    );

    return videoSnaps
      .filter((snap) => snap.exists())
      .map((snap) => ({
        id: snap.id,
        ...convertVideoData(snap.data()),
      })) as (VideoMeta & { id: string })[];
  } catch (error) {
    console.error("Error getting videos by references:", error);
    throw error;
  }
}

/**
 * 通過 drama_id 獲取影集（備用方式）
 */
export async function getVideosByDramaId(
  dramaId: string
): Promise<(VideoMeta & { id: string })[]> {
  try {
    const videosRef = collection(db, "videos");
    const q = query(
      videosRef,
      where("drama_id", "==", dramaId),
      orderBy("order", "asc")
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertVideoData(doc.data()),
    })) as (VideoMeta & { id: string })[];
  } catch (error) {
    console.error("Error getting videos by drama_id:", error);
    throw error;
  }
}

/**
 * 獲取單個影集
 */
export async function getVideo(id: string): Promise<(VideoMeta & { id: string }) | null> {
  try {
    const videoRef = doc(db, "videos", id);
    const videoSnap = await getDoc(videoRef);

    if (videoSnap.exists()) {
      return {
        id: videoSnap.id,
        ...convertVideoData(videoSnap.data()),
      } as VideoMeta & { id: string };
    }

    return null;
  } catch (error) {
    console.error("Error getting video:", error);
    throw error;
  }
}

/**
 * 創建影集
 *
 * Firestore doc id 一律用 vid（BytePlus 影片 ID），與後端 byteplus.ts callback
 * 建 doc 時的策略一致 — likeCount trigger 寫 `videos/{vid}.likeCount`，
 * 若這裡用隨機 id（addDoc）會讓 trigger 找不到對應 doc，喜愛數永遠是 0。
 */
export async function createVideo(
  data: Omit<VideoMeta, "id" | "createdAt" | "updatedAt">
): Promise<string> {
  try {
    const vid = (data.vid || "").trim();
    if (!vid) {
      throw new Error("缺少 vid，無法建立影集（請先在 BytePlus 後台上傳取得 vid）");
    }

    const videoRef = doc(db, "videos", vid);

    // 防止覆寫既有同 vid 文檔（同 vid 重複新增是 admin 操作失誤）
    const existing = await getDoc(videoRef);
    if (existing.exists()) {
      throw new Error(`vid "${vid}" 已存在於 videos collection，請確認是否重複新增`);
    }

    const videoData: any = {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // 轉換 scheduled_release_time 為 Timestamp（如果存在）
    if (data.scheduled_release_time instanceof Date) {
      videoData.scheduled_release_time = Timestamp.fromDate(data.scheduled_release_time);
    }

    // 集級覆寫價：null/0/非正數 → 不寫入（沿用劇層預設）；有效正數 → 寫入
    const epCost = (videoData as any).episode_unlock_coin_cost;
    if (epCost === null || typeof epCost !== "number" || epCost <= 0) {
      delete videoData.episode_unlock_coin_cost;
    }

    // 選集標籤：空字串/null → 不寫入（依集型自動編號）；有值 → trim 後寫入
    const epLabel = (videoData as any).episode_label;
    if (typeof epLabel === "string" && epLabel.trim() !== "") {
      videoData.episode_label = epLabel.trim();
    } else {
      delete videoData.episode_label;
    }

    // 集型：normal 為預設（不寫入，省欄位）；special/trailer 才寫入
    const epType = (videoData as any).episode_type;
    if (epType === "special" || epType === "trailer") {
      videoData.episode_type = epType;
    } else {
      delete videoData.episode_type;
    }

    // Firestore setDoc() 不接受 undefined，需先移除以避免建立失敗
    for (const key of Object.keys(videoData)) {
      if (videoData[key] === undefined) {
        delete videoData[key];
      }
    }

    await setDoc(videoRef, videoData);
    return vid;
  } catch (error) {
    console.error("Error creating video:", error);
    throw error;
  }
}

type VideoUpdateData = {
  [K in keyof Omit<VideoMeta, "id" | "createdAt" | "updatedAt">]?:
    | Omit<VideoMeta, "id" | "createdAt" | "updatedAt">[K]
    | FieldValue;
};

/**
 * 更新影集
 */
export async function updateVideo(
  id: string,
  data: VideoUpdateData
): Promise<void> {
  try {
    const videoRef = doc(db, "videos", id);
    // 計數欄位由 trigger / adjustDisplayMetric 維護；表單帶回的是讀取當下的舊值，寫回會蓋掉期間新增的數字
    const { play_times: _playTimes, like: _like, likeCount: _likeCount, ...rest } = data as VideoUpdateData & {
      likeCount?: unknown;
    };
    const updateData: any = {
      ...rest,
      updatedAt: Timestamp.now(),
    };

    // 轉換 scheduled_release_time 為 Timestamp（如果存在）
    if (data.scheduled_release_time instanceof Date) {
      updateData.scheduled_release_time = Timestamp.fromDate(data.scheduled_release_time);
    }

    // 集級覆寫價：null/0/非正數 → deleteField()（沿用劇層預設），有效正數 → 寫入
    if ("episode_unlock_coin_cost" in data) {
      const cost = data.episode_unlock_coin_cost as number | null | undefined;
      if (cost === null || cost === undefined || typeof cost !== "number" || cost <= 0) {
        updateData.episode_unlock_coin_cost = deleteField();
      } else {
        updateData.episode_unlock_coin_cost = cost;
      }
    }

    // 選集標籤：空字串/null → deleteField()（依集型自動編號），有值 → trim 後寫入
    if ("episode_label" in data) {
      const label = data.episode_label as string | null | undefined;
      if (typeof label === "string" && label.trim() !== "") {
        updateData.episode_label = label.trim();
      } else {
        updateData.episode_label = deleteField();
      }
    }

    // 集型：special/trailer 寫入；normal/空 → deleteField()（預設正片）
    if ("episode_type" in data) {
      const t = data.episode_type as string | null | undefined;
      if (t === "special" || t === "trailer") {
        updateData.episode_type = t;
      } else {
        updateData.episode_type = deleteField();
      }
    }

    // Firestore updateDoc() 不接受 undefined，需先移除以避免更新失敗
    for (const key of Object.keys(updateData)) {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    }

    await updateDoc(videoRef, updateData);
  } catch (error) {
    console.error("Error updating video:", error);
    throw error;
  }
}

/**
 * 刪除影集
 */
export async function deleteVideo(id: string): Promise<void> {
  try {
    const videoRef = doc(db, "videos", id);
    await deleteDoc(videoRef);
  } catch (error) {
    console.error("Error deleting video:", error);
    throw error;
  }
}

